import { useCallback } from "react"

export default function useNewAppointmentController({
  clinicId,
  treatments,
  selectedAppointment,
  formatDateTimeLocal,
  setNewAppt,
  setDoctorSuggestion,
  setAlternatives,
  setPatientQuery,
  setQuickPatient,
  setShowNewModal,
  setPatientWorkspaceOpen,
  isReadOnly,
  readOnlyReason
}) {
  const resetQuickPatientState = useCallback(() => {
    setPatientQuery("")
    setQuickPatient({
      firstName: "",
      lastName: "",
      phone: "",
      email: ""
    })
  }, [setPatientQuery, setQuickPatient])

  const resetNewAppointmentState = useCallback(() => {
    setDoctorSuggestion(null)
    setAlternatives([])
    resetQuickPatientState()

    setNewAppt({
      patientId: "",
      treatmentId: "",
      doctorId: "",
      chairId: "",
      start: "",
      duration: 30,
      treatmentPlanItemId: "",
      planTitle: ""
    })
  }, [
    resetQuickPatientState,
    setDoctorSuggestion,
    setAlternatives,
    setNewAppt
  ])

  const openNewAppointmentModal = useCallback(() => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    setShowNewModal(true)
  }, [isReadOnly, readOnlyReason, setShowNewModal])

  const openEmptyNewAppointment = useCallback(() => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    resetNewAppointmentState()
    openNewAppointmentModal()
  }, [isReadOnly, readOnlyReason, resetNewAppointmentState, openNewAppointmentModal])

  const handleDateClick = useCallback((info) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    if (!clinicId || clinicId === "ALL") {
      alert("Selecciona una clínica para crear cita manual inteligente")
      return
    }

    const startStr = formatDateTimeLocal(info.date)

    setNewAppt({
      patientId: "",
      treatmentId: "",
      doctorId: "",
      chairId: "",
      start: startStr,
      duration: 30,
      treatmentPlanItemId: "",
      planTitle: ""
    })

    setDoctorSuggestion(null)
    setAlternatives([])
    resetQuickPatientState()
    setShowNewModal(true)
  }, [
    isReadOnly,
    readOnlyReason,
    clinicId,
    formatDateTimeLocal,
    resetQuickPatientState,
    setAlternatives,
    setDoctorSuggestion,
    setNewAppt,
    setShowNewModal
  ])

  const onTreatmentChange = useCallback((treatmentId) => {
    const treatment = treatments.find((x) => x.id === treatmentId)

    setNewAppt((prev) => ({
      ...prev,
      treatmentId,
      duration: treatment?.durationMinutes || 30,
      doctorId: prev.doctorId || "",
      start: prev.start || ""
    }))

    setDoctorSuggestion(null)
    setAlternatives([])
  }, [
    treatments,
    setNewAppt,
    setDoctorSuggestion,
    setAlternatives
  ])

  const scheduleFromPlanItem = useCallback((item) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    if (item.appointmentId) {
      alert("Este tratamiento ya tiene una cita programada")
      return
    }

    const treatment = treatments.find((t) => t.id === item.treatmentId)
    const duration = treatment?.durationMinutes || 30

    setNewAppt((prev) => ({
      ...prev,
      patientId: selectedAppointment?.patientId || "",
      treatmentId: item.treatmentId || "",
      doctorId: item.assignedDoctorId || item.suggestedDoctorId || "",
      chairId: "",
      start: "",
      duration,
      treatmentPlanItemId: item.id,
      planTitle: item.planTitle || ""
    }))

    setDoctorSuggestion(null)
    setAlternatives([])
    resetQuickPatientState()
    setShowNewModal(true)
    setPatientWorkspaceOpen(false)
  }, [
    isReadOnly,
    readOnlyReason,
    treatments,
    selectedAppointment,
    resetQuickPatientState,
    setNewAppt,
    setDoctorSuggestion,
    setAlternatives,
    setShowNewModal,
    setPatientWorkspaceOpen
  ])

  const openFromOpportunityGap = useCallback((gapStart) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return
    }

    const startStr = formatDateTimeLocal(gapStart)

    setNewAppt({
      patientId: "",
      treatmentId: "",
      doctorId: "",
      chairId: "",
      start: startStr,
      duration: 30,
      treatmentPlanItemId: "",
      planTitle: ""
    })

    setDoctorSuggestion(null)
    setAlternatives([])
    resetQuickPatientState()
    setShowNewModal(true)
  }, [
    isReadOnly,
    readOnlyReason,
    formatDateTimeLocal,
    resetQuickPatientState,
    setNewAppt,
    setDoctorSuggestion,
    setAlternatives,
    setShowNewModal
  ])

  return {
    resetNewAppointmentState,
    openEmptyNewAppointment,
    handleDateClick,
    onTreatmentChange,
    scheduleFromPlanItem,
    openFromOpportunityGap
  }
}